import { format, parseISO } from 'date-fns';

/**
 * Build options for the Data History bar chart.
 *
 * The app runs Chart.js v4, which reads `scales.x` / `scales.y` and
 * `ticks.color`. The previous config used Chart.js v2 keys (`xAxis` / `yAxis`,
 * `ticks.fontColor`), which v4 silently ignores — so neither the configured
 * y-axis range/step nor the tick label color were ever applied. This emits the
 * equivalent v4 options.
 *
 * @param options.max/min/stepSize/maxTicksLimit - y-axis range and tick density.
 * @param options.textColor - axis tick label color. Omit to keep Chart.js'
 *   default (light mode); pass a light color in dark mode for legibility.
 * @see https://www.chartjs.org/docs/4.5.0/axes/cartesian/linear.html
 */
export const generateBarChartOptions = (options: {
  maxTicksLimit?: number;
  max: number;
  min: number;
  stepSize?: number;
  textColor?: string;
}) => {
  const { max, min, stepSize, maxTicksLimit, textColor } = options;

  // Respect explicit 0s while keeping NaN/Infinity out of Chart.js: only fall
  // back when the value isn't a finite number.
  const finiteOr = (value: number | undefined, fallback: number) =>
    typeof value === 'number' && Number.isFinite(value) ? value : fallback;
  const resolvedMin = finiteOr(min, 0);
  const resolvedMax = finiteOr(max, 100);

  // `??` (not `||`) so an explicit 0 step is preserved; a 0 step is then dropped
  // below so Chart.js auto-picks the interval.
  const resolvedStepSize = stepSize ?? Math.floor((resolvedMax - resolvedMin) / 5);

  const yTicks: Record<string, unknown> = { maxTicksLimit: maxTicksLimit ?? 5 };
  if (resolvedStepSize > 0) yTicks.stepSize = resolvedStepSize;
  if (textColor) yTicks.color = textColor;

  const xTicks: Record<string, unknown> = {};
  if (textColor) xTicks.color = textColor;

  return {
    maintainAspectRatio: false,
    scales: {
      x: { ticks: xTicks },
      y: {
        min: resolvedMin,
        max: resolvedMax,
        ticks: yTicks,
      },
    },
  };
};

/**
 *
 * @param startDate
 * @param endDate
 * @returns date in format of "MMM D - MMM D"
 */
export const formatChartDateRange = ({
  startDate,
  endDate,
}: {
  startDate: string;
  endDate: string;
}) => {
  const formattedStartDate = format(parseISO(startDate), 'MMM d');
  const formattedEndDate = format(parseISO(endDate), 'MMM d');

  return `${formattedStartDate} - ${formattedEndDate}`;
};
